import React, { useEffect, useRef } from "react";
import GoogleMapReact from "google-map-react";

import { useAppSelector } from "../../../app/hooks";
import { OptimizedPlan, OptimizedStop } from "../../../models";
import { dumpsterSizeToText } from "../../../models/Order";
import { routeColor } from "./OptionDetail";

import * as simpleMapStyle from "../../Schedules/components/MapView/simpleStyle.json";

// ---------------------------------------------------------------------------
// Pin — a dot (with an optional short label like "S") in the "all" overview, or
// a labeled pill with a tail (+ optional ETA) for a single focused route. `title`
// is the hover tooltip (name · address · size · ETA).
// ---------------------------------------------------------------------------

interface PinProps {
  lat: number;
  lng: number;
  label: string;
  color: string;
  title?: string;
  eta?: string;
  sizeLabel?: string;
  dimmed?: boolean;
  emphasized?: boolean;
  asDot?: boolean;
}

function Pin({ label, color, title, eta, sizeLabel, dimmed, emphasized, asDot }: PinProps) {
  if (asDot) {
    const hasLabel = !!label;
    // Labeled dots (start + numbered stops) run a touch larger so 2-digit
    // sequence numbers stay legible; bare facility dots stay small.
    const size = emphasized
      ? hasLabel
        ? 26
        : 22
      : dimmed
      ? 9
      : hasLabel
      ? 18
      : 14;
    const fontSize = emphasized ? 12 : label.length >= 2 ? 8 : 9;
    return (
      <div
        title={title}
        style={{
          transform: "translate(-50%, -50%)",
          width: size,
          height: size,
          borderRadius: "50%",
          background: color,
          border: "2px solid #fff",
          opacity: dimmed ? 0.35 : 1,
          boxShadow: "0 1px 2px rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize,
          fontWeight: 700,
          lineHeight: 1,
          zIndex: emphasized ? 20 : hasLabel ? 2 : 1,
        }}
      >
        {label}
      </div>
    );
  }
  return (
    <div
      title={title}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        transform: "translate(-50%, -100%)",
      }}
    >
      <div
        style={{
          background: color,
          color: "#fff",
          border: "2px solid #fff",
          borderRadius: 14,
          minWidth: 24,
          height: 24,
          padding: "0 7px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          whiteSpace: "nowrap",
          boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          width: 0,
          height: 0,
          borderLeft: "5px solid transparent",
          borderRight: "5px solid transparent",
          borderTop: `8px solid ${color}`,
        }}
      />
      {sizeLabel && (
        <span
          style={{
            marginTop: 1,
            padding: "0 3px",
            borderRadius: 3,
            background: "#fff",
            color: "#222",
            border: "1px solid rgba(0,0,0,0.25)",
            fontSize: 9,
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          {sizeLabel}
        </span>
      )}
      {eta && (
        <span
          style={{
            marginTop: 1,
            padding: "0 3px",
            borderRadius: 3,
            background: "rgba(255,255,255,0.85)",
            color: "#333",
            fontSize: 9,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {eta}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface Coord {
  lat: number;
  lng: number;
}

type FacilityType = "dump_yard" | "storage";

interface Marker extends Coord {
  label: string;
  color: string;
  routeIdx: number;
  title?: string;
  eta?: string;
  sizeLabel?: string;
  facilityType?: FacilityType;
}

const num = (v: any): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const stopCoord = (stop: OptimizedStop): Coord | null => {
  const lat = num(stop.lat);
  const lng = num(stop.lng);
  if (lat == null || lng == null || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
};

// Stop-type colors, matching the Schedules map ($color-blue / $color-orange /
// $color-dumpyard / $color-storage); driver_start is a neutral.
const TYPE_COLORS: Record<string, string> = {
  delivery: "#0A60BB",
  pickup: "#FEAA2B",
  dump_yard: "#013220",
  storage: "#A89D92",
  driver_start: "#5A6473",
};
const DUMP_YARD_COLOR = TYPE_COLORS.dump_yard;
const STORAGE_COLOR = TYPE_COLORS.storage;
const isFacility = (t: string) => t === "dump_yard" || t === "storage";

export interface RouteMapProps {
  plan: OptimizedPlan;
  focusedRouteIndex: number | "all";
  highlightedRouteIndex?: number | null;
  highlightedFacility?: FacilityType | null;
}

export function RouteMap({
  plan,
  focusedRouteIndex,
  highlightedRouteIndex,
  highlightedFacility,
}: RouteMapProps) {
  const allDeliveries = useAppSelector((s) => s.deliveries.deliveries);
  const allPickups = useAppSelector((s) => s.pickups.pickups);
  const allTemplates = useAppSelector((s) => s.dumpYards.templates);
  const drivers = useAppSelector((s) => s.driversV2.drivers) || [];
  const mapApi = useRef<{ map: any; maps: any } | null>(null);

  // Name / address / dumpster size for a stop, from the loaded records.
  const meta = (stop: OptimizedStop): { name: string; address: string; size: string } => {
    const sizeText = (s: any): string =>
      s ? (dumpsterSizeToText as Record<string, string>)[s] ?? String(s) : "";
    if (stop.type === "delivery") {
      const d = allDeliveries.find((x) => x.id === stop.id);
      if (d) return { name: d.name, address: d.address, size: sizeText(d.dumpsterSize) };
    } else if (stop.type === "pickup") {
      const p = allPickups.find((x) => x.id === stop.id);
      if (p) return { name: p.name, address: p.address, size: sizeText(p.dumpsterSize) };
    } else if (isFacility(stop.type)) {
      const t = allTemplates.find((x) => x.id === stop.id);
      if (t) return { name: t.name, address: t.address, size: "" };
    } else if (stop.type === "driver_start") {
      const dr = drivers.find((x: any) => x.userId === stop.id || x.id === stop.id);
      if (dr) return { name: dr.name, address: dr.address, size: "" };
    }
    return { name: `${stop.type} #${stop.id}`, address: "", size: "" };
  };

  const titleFor = (stop: OptimizedStop): string => {
    const m = meta(stop);
    return [m.name, m.address, m.size, stop.eta ? `ETA ${stop.eta}` : ""]
      .filter(Boolean)
      .join(" · ");
  };

  const isAll = focusedRouteIndex === "all";
  const markers: Marker[] = [];

  if (isAll) {
    // Shared facilities — type color, collapsed globally.
    const facilities = new Map<
      string,
      { lat: number; lng: number; type: FacilityType; title: string }
    >();
    plan.routes.forEach((route) => {
      route.stops.forEach((stop) => {
        if (!isFacility(stop.type)) return;
        const coord = stopCoord(stop);
        if (!coord) return;
        const key = `${coord.lat.toFixed(5)},${coord.lng.toFixed(5)}`;
        if (!facilities.has(key)) {
          facilities.set(key, {
            lat: coord.lat,
            lng: coord.lng,
            type: stop.type as FacilityType,
            title: titleFor(stop),
          });
        }
      });
    });
    facilities.forEach((f) =>
      markers.push({
        lat: f.lat,
        lng: f.lng,
        label: "",
        color: f.type === "dump_yard" ? DUMP_YARD_COLOR : STORAGE_COLOR,
        routeIdx: -1,
        facilityType: f.type,
        title: f.title,
      })
    );

    // Per driver: start ("S") + numbered job dots, in the driver's color — the
    // numbers give the visit order so you can trace each route on the overview.
    plan.routes.forEach((route, idx) => {
      const color = routeColor(idx);
      const seen = new Set<string>();
      let seq = 0;
      route.stops.forEach((stop) => {
        if (isFacility(stop.type)) return;
        const isStart = stop.type === "driver_start";
        if (!isStart) seq++;
        const coord = stopCoord(stop);
        if (!coord) return;
        const key = `${coord.lat.toFixed(5)},${coord.lng.toFixed(5)}`;
        if (seen.has(key)) return;
        seen.add(key);
        markers.push({
          lat: coord.lat,
          lng: coord.lng,
          label: isStart ? "S" : String(seq),
          color,
          routeIdx: idx,
          title: titleFor(stop),
        });
      });
    });
  } else {
    // Single focused route: collapse co-located stops into one labeled pin,
    // colored by stop type (delivery blue / pickup orange / …).
    const route = plan.routes[focusedRouteIndex as number];
    const groups = new Map<
      string,
      {
        lat: number;
        lng: number;
        labels: string[];
        type: string;
        etas: string[];
        sizes: string[];
        title: string;
      }
    >();
    let seqNum = 0;
    route?.stops.forEach((stop) => {
      const isStart = stop.type === "driver_start";
      if (!isStart) seqNum++;
      const label = isStart ? "S" : String(seqNum);
      const coord = stopCoord(stop);
      if (!coord) return;
      const size = meta(stop).size;
      const key = `${coord.lat.toFixed(5)},${coord.lng.toFixed(5)}`;
      const g = groups.get(key);
      if (g) {
        if (!g.labels.includes(label)) g.labels.push(label);
        if (stop.eta && !g.etas.includes(stop.eta)) g.etas.push(stop.eta);
        if (size && !g.sizes.includes(size)) g.sizes.push(size);
      } else {
        groups.set(key, {
          lat: coord.lat,
          lng: coord.lng,
          labels: [label],
          type: stop.type,
          etas: stop.eta ? [stop.eta] : [],
          sizes: size ? [size] : [],
          title: titleFor(stop),
        });
      }
    });
    groups.forEach((g) => {
      const start = g.labels.filter((l) => l === "S");
      const nums = g.labels
        .filter((l) => l !== "S")
        .map(Number)
        .sort((a, b) => a - b)
        .map(String);
      markers.push({
        lat: g.lat,
        lng: g.lng,
        label: [...start, ...nums].join(","),
        color: TYPE_COLORS[g.type] ?? routeColor(focusedRouteIndex as number),
        routeIdx: focusedRouteIndex as number,
        eta: g.etas.join(", "),
        sizeLabel: g.sizes.join(", "),
        title: g.title,
      });
    });
  }

  const fit = () => {
    if (!mapApi.current || markers.length === 0) return;
    const { map, maps } = mapApi.current;
    if (markers.length === 1) {
      map.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
      map.setZoom(13);
      return;
    }
    const bounds = new maps.LatLngBounds();
    markers.forEach((m) => bounds.extend(new maps.LatLng(m.lat, m.lng)));
    map.fitBounds(bounds);
  };

  useEffect(() => {
    fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedRouteIndex, markers.length]);

  const stateOf = (m: Marker): { dim: boolean; emph: boolean } => {
    if (highlightedFacility) {
      if (m.facilityType) {
        return {
          dim: m.facilityType !== highlightedFacility,
          emph: m.facilityType === highlightedFacility,
        };
      }
      return { dim: true, emph: false };
    }
    if (highlightedRouteIndex != null) {
      if (m.facilityType) return { dim: false, emph: false };
      return {
        dim: m.routeIdx !== highlightedRouteIndex,
        emph: m.routeIdx === highlightedRouteIndex,
      };
    }
    return { dim: false, emph: false };
  };

  return (
    <GoogleMapReact
      bootstrapURLKeys={{
        key: "AIzaSyCkRYkFYLEOcaEbwmSYJXaQ2jlIttKkl8M",
        version: "3.58.8",
      }}
      defaultCenter={{ lat: 40.40016, lng: -80.04932 }}
      defaultZoom={11}
      options={{ styles: simpleMapStyle }}
      yesIWantToUseGoogleMapApiInternals
      onGoogleApiLoaded={({ map, maps }) => {
        mapApi.current = { map, maps };
        fit();
      }}
    >
      {markers.map((m, i) => {
        const { dim, emph } = stateOf(m);
        return (
          <Pin
            key={i}
            lat={m.lat}
            lng={m.lng}
            label={m.label}
            color={m.color}
            title={m.title}
            eta={!isAll ? m.eta : undefined}
            sizeLabel={!isAll ? m.sizeLabel : undefined}
            dimmed={dim}
            emphasized={emph}
            asDot={isAll}
          />
        );
      })}
    </GoogleMapReact>
  );
}
